import '/assets/styles/pages/basket.scss';
import { useEffect, useMemo, useState } from 'react';
import config from '@wearetla/tla-essentials-tools/config';
import omit from 'lodash/omit';

// Context
import { useBranchSelection } from '/utilities/branch-selection';
import { useBasket, useBasketProductControls } from '/controllers/basket';

// Sections
import PageHeader from '/views/sections/page-header';
import BasketControls from '/views/sections/basket-controls';

// Partials
import Loader from '/views/partials/loader';
import Btn from '/views/partials/btn';
import Link from '@wearetla/tla-essentials-tools/partials/link';
import Pricebox from '/views/partials/pricebox';
import Img from '@wearetla/tla-essentials-tools/partials/img';
import EmptyMessage from '/views/partials/empty-message';
import { Input } from '/views/partials/forms';

const Basket = () => {
	const { branchTableTitle, selectedBranch, checkoutEnabled } = useBranchSelection();
	const { items, busy, prepareSale, initialized } = useBasket();

	const [rawProductNotes, setProductNotes] = useState(null);

	const productNotes = useMemo(() => rawProductNotes ?? {}, [rawProductNotes]);

	useEffect(() => {
		if (rawProductNotes) {
			localStorage.setItem(config.productNotesPersistenceKey, JSON.stringify(productNotes));
		}
	}, [rawProductNotes]);

	useEffect(() => {
		const rawPersistenceData = localStorage.getItem(config.productNotesPersistenceKey);

		if (rawPersistenceData) {
			try {
				const parsedPersistenceData = JSON.parse(rawPersistenceData);
				if (typeof parsedPersistenceData === 'object' && !Array.isArray(parsedPersistenceData)) {
					setProductNotes(parsedPersistenceData);
				}
			} catch (e) {
				localStorage.removeItem(config.productNotesPersistenceKey);
			}
		}
	}, []);

	const submitBasket = () => {
		prepareSale(productNotes);
	};

	return (
		<>
			<PageHeader
				icon="menu"
				backLink="home"
				backLinkText
				title="Sepetim"
				subtitle={branchTableTitle}
			/>

			<div className="basket-items loader-container">
				<Loader
					solid
					dark
					message="Sepetiniz getiriliyor..."
					loading={!initialized || busy} />
				{checkoutEnabled ?
					<>
						{items.map((item) => (
							<BasketProduct
								key={item.id}
								item={item}
								productNotes={productNotes?.[item.id] ?? ''}
								onNoteChange={(newNote) => {
									if (newNote?.length) {
										setProductNotes((currentNotes) => ({
											...currentNotes,
											[item.id]: newNote,
										}));
									} else {
										setProductNotes((currentNotes) => omit(currentNotes, [item.id]));
									}
								}}
							/>
						))}
						{(initialized && !items.length) && (
							<div className="wrapper">
								<EmptyMessage className="basket-emptymessage">
									Sepetinizde ürün bulunmuyor.
								</EmptyMessage>
							</div>
						)}
					</>
					:
					<div className="wrapper">
						<EmptyMessage className="basket-emptymessage">
							<p><strong>{selectedBranch?.title}</strong> mağazamızda ne yazık ki uygulama üzerinden sipariş alamıyoruz.</p>
							<p>Mağazamızdaki kiosk ve kasalardan sipariş verebilirsiniz.</p>
						</EmptyMessage>
					</div>
				}
			</div>

			<BasketControls loading={busy} onSubmit={submitBasket} />
		</>
	);
};

const BasketProduct = ({ item, productNotes, onNoteChange }) => {
	const { busy } = useBasket();
	const { canIncreaseQuantity, canDecreaseQuantity, increaseQuantity, decreaseQuantity, quantity } =
		useBasketProductControls(item);

	return (
		<div className="items-item">
			<div className="item-wrap wrapper">
				<div className="item-imagewrap">
					<span className="imagewrap-quantity">{quantity}x</span>
					<Img src={item.image} className="item-image" />
				</div>
				<div className="item-textarea">
					<Link className="item-title" href="menuProduct" params={{ id: item.id }}>
						{item.title}
					</Link>
					<div className="item-singleprice">
						<Pricebox horizontal price={item.price} />/ad
					</div>
					<Pricebox className="item-price" product={item} />
				</div>
				<div className="item-controls">
					<Btn
						className="primary"
						icon="plus"
						disabled={!canIncreaseQuantity || busy}
						onClick={increaseQuantity}
					/>
					<Btn
						className="primary"
						icon="minus"
						disabled={!canDecreaseQuantity || busy}
						onClick={() => {
							decreaseQuantity(true);
						}}
					/>
				</div>

				<div className="item-notes">
					<Input
						type="textarea"
						value={productNotes}
						onChange={onNoteChange}
						placeholder="Ürün için sipariş notlarınız."
						className="notes-input"
					/>
				</div>
			</div>
		</div>
	);
};

export default Basket;
